import React, { Component } from "react";
import { Card, Row, Typography, Input } from "antd";
import { HeartOutlined } from "@ant-design/icons";
import { useRouter } from "next/router";
import { getPurOrg } from "../../../utils";
import { startPaiement } from "../../../services/paiementLauncher";
import {normalizeDonationPresets} from "../../../components/donationPresets";
import { getCurrencyFormater, getDeviseSymbol } from "../../../i18n";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import Progress from "../components/Progress";

const { Text } = Typography;

/**
 * Variante 2026 de CardDon (forkée de themes/Travel/Don/CardDon).
 * Différences avec Travel :
 *   - Progress Antd remplacée par le composant Progress 2026 (gradient
 *     primary→secondary, shimmer animé, glow pulsant)
 *   - Aucune autre modification fonctionnelle : même logique de validation,
 *     même flux paiement, mêmes preset buttons.
 *
 * Si le flux de don évolue côté Travel (preset buttons API, smart caisses,
 * MultiDevise, etc.), il faudra reporter les changements ici.
 * À factoriser dans themes/shared/ quand on aura le temps.
 */
class CardDon extends Component {

    amountInput = React.createRef();

    state = {
        amountError: false,
        inputValue: "",
        montant: null,
        returnUrl: "",
    };

    componentDidMount() {
        this.setState({ returnUrl: window.location.href });
    }

    onValidate() {
        const raw = String(this.state.inputValue ?? "");
        const cleaned = raw.replace(",", ".");
        const finalMontant = parseFloat(cleaned);

        if (!Number.isFinite(finalMontant) || finalMontant < 1) {
            this.setState({amountError: true}, () => this.amountInput.current?.focus());
            return false;
        }

        const router = this.props.router;
        const context = this.props.context;
        const { org } = context;

        let object = {
            montant: finalMontant,
            returnUrl: this.state.returnUrl,
            organisation: getPurOrg(org),
            devise: org.devise,
            type: "GENERAL",
            typeId: null,
            cerfa: true,
        };

        if (this.props.caisse) {
            const caisse = { ...this.props.caisse };
            delete caisse.textContent;
            object.type = "CAISSE";
            object.typeId = caisse.id;
            object.caisse = caisse;
            if (caisse.cerfa !== null && caisse.cerfa !== undefined) {
                object.cerfa = caisse.cerfa;
            }
        }

        const accessId = context.query.accessId || context.query.a;
        if (accessId) object.accessId = accessId;

        // Centralisé : source (APP/WEB) + returnUrl (deep link en app) + sessionStorage
        // + redirect. Cf. services/paiementLauncher.
        startPaiement(object, { org, router });
    }

    renderButtons() {
        const caisse = this.props.caisse;
        const context = this.props.context;
        const { config } = context;
        const currencyFormater = getCurrencyFormater(context);

        const defaultButtons = [50, 100, 250, 500, 1000, 2000].map(i => ({ text: currencyFormater(i), montant: i }));
        let finalButtons = defaultButtons;

        if (!caisse && Array.isArray(config["page.don.presetButtons"]) && config["page.don.presetButtons"].length > 0) {
            finalButtons = config["page.don.presetButtons"];
        }
        if (caisse && Array.isArray(caisse.presetButtons) && caisse.presetButtons.length > 0) {
            finalButtons = caisse.presetButtons;
        }

        finalButtons = normalizeDonationPresets(finalButtons);

        return (
            <div style={{
                marginTop: 10,
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))", width: "80%", maxWidth: "100%", minWidth: 0,
                gap: 15,
            }}>
                {finalButtons.map((i, idx) => {
                    const label = i.text || currencyFormater(i.montant);
                    return (
                        <button type="button"
                            key={idx}
                            aria-pressed={this.state.inputValue === i.montant?.toString()}
                            onClick={() => this.setState({ inputValue: i.montant?.toString(), amountError: false })}
                            style={{
                                cursor: "pointer", border: 0, background: "transparent", color: "inherit", font: "inherit", minHeight: 44,
                                boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 7,
                                padding: "10px 8px", minWidth: 0, overflowWrap: "anywhere", textAlign: "center",
                            }}
                        >
                            <span style={{ fontSize: 15, fontWeight: 600 }}>{label}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    render() {
        const caisse = this.props.caisse;
        const context = this.props.context;
        const { org } = context;

        const currencyFormater = getCurrencyFormater(context);

        let displayTotal = 0;
        let percent = 0;
        let displayReste = 0;
        let displayTarget = 0;

        const name = caisse ? caisse.name : org.name;

        if (caisse) {
            displayTotal = caisse.total + caisse.marge;

            if (caisse.withObjectif) {
                displayTarget = caisse.objectif + caisse.marge;
                displayReste = displayTotal < displayTarget ? displayTarget - displayTotal : 0;
                percent = displayTotal * 100 / displayTarget;

                if (caisse.smart) {
                    displayTarget = caisse.objectif;
                    displayReste = parseFloat(caisse.reste).toFixed(2);
                    if (caisse.reste > caisse.objectif) {
                        percent = 0;
                    }
                }
            }
        }

        return (
            <div className="unisoft-donation-card">
                <style jsx global>{`
                  .unisoft-donation-card button:focus-visible, .unisoft-donation-card input:focus-visible {outline:3px solid var(--primary, #2B70A9);outline-offset:3px}
                  @media(prefers-reduced-motion:reduce){.unisoft-donation-card button{transition:none!important;transform:none!important}}
                `}</style>
                <Card
                    style={{ minWidth: 0, maxWidth: "100%", boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" }}
                    bodyStyle={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingLeft: 0,
                        paddingRight: 0,
                    }}
                >
                    <img src={org.logo} style={{ height: "auto", width: "50%" }} alt={org.name} />

                    <h3 style={{ marginTop: 20, textAlign: "center" }}>{name}</h3>

                    {(!!caisse && caisse.withObjectif) && (
                        <div style={{ width: "80%", margin: "15px 0" }}>
                            <Row justify={"space-between"} style={{ marginBottom: 8 }}>
                                <Text><Trans>Objectif</Trans>: {currencyFormater(displayTarget, 0, context.org.orgDevise)}</Text>
                                <Text><Trans>Reste</Trans>: {currencyFormater(displayReste, 0, context.org.orgDevise)}</Text>
                            </Row>
                            <Progress percent={percent} />
                        </div>
                    )}

                    {(!!caisse && !caisse.withObjectif && caisse.forceShowTotal) && (
                        <div style={{ width: "70%", margin: "15px 0" }}>
                            <Row justify={"center"}>
                                <Text><Trans>Montant Total Collecté</Trans>: {currencyFormater(displayTotal, 0, context.org.orgDevise)}</Text>
                            </Row>
                        </div>
                    )}

                    {this.renderButtons()}

                    <Card
                        bodyStyle={{ padding: "10px 15px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        style={{ marginTop: 20, width: "80%", boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" }}
                    >
                        <Input ref={this.amountInput} inputMode="decimal" aria-label={t`Autre montant`} aria-invalid={this.state.amountError || undefined}
                            value={this.state.inputValue}
                            placeholder={t`Autre montant`}
                            style={{
                                textAlign: "center",
                                width: 0, flex: "1 1 0", minWidth: 0,
                                fontSize: 22,

                                backgroundColor: "transparent",
                                border: 0,
                            }}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (/^[0-9]*[.,]?[0-9]*$/.test(raw)) {
                                    this.setState({ inputValue: raw, amountError: false });
                                }
                            }}
                        />
                        <span style={{ fontSize: 22, flexShrink: 0 }}>{getDeviseSymbol(context)}</span>
                    </Card>

                    {this.state.amountError && <p role="alert" style={{marginBlock: "12px 0", width: "80%"}}><Text type="danger">{t`Montant invalide`}</Text></p>}

                    <button type="button"
                        onClick={() => this.onValidate()}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "rgba(0, 0, 0, 0.18) 0px 12px 28px 0px";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "rgba(0, 0, 0, 0.12) 0px 7px 20px 0px";
                        }}
                        style={{
                            cursor: "pointer",
                            marginTop: 20,
                            width: "80%",
                            minHeight: 52, height: "auto", padding: "12px 10px", minWidth: 0, maxWidth: "100%", whiteSpace: "normal",
                            border: 0,

                            background: "var(--primary, #2B70A9)",
                            color: "#FFF",
                            fontSize: 18,
                            fontWeight: 600,
                            letterSpacing: 0.3,
                            borderRadius: 12,
                            boxShadow: "rgba(0, 0, 0, 0.12) 0px 7px 20px 0px",
                            transition: "transform 0.18s ease, box-shadow 0.18s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 10,
                        }}
                    >
                        <HeartOutlined style={{ fontSize: 20 }} />
                        <Trans>Valider mon don</Trans>
                    </button>
                </Card>
            </div>
        );
    }
}

function withHook(Comp) {
    return (props) => {
        const router = useRouter();
        return <Comp router={router} {...props} />;
    };
}

export default withHook(CardDon);
